import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { TrailLink } from "@/lib/pageTrail";

// How a page names itself in the top bar's breadcrumb (BRK-S269;
// src/lib/pageTrail.ts). Most pages needn't: the sidebar and the route table
// name them. A record does, since its name is the breadcrumb's last part
// ("Accounts / Cedarline Contracting LLC"), and BaseForm does it for every
// record. A record that sits under ANOTHER record names that ancestry too
// (BRK-S399: a bond submission under its account). The name and its setter
// are separate contexts, so the pages that set a name don't re-render when it
// changes.
interface Named {
  name?: string;
  via?: readonly TrailLink[];
}

const NameContext = createContext<Named>({});
const SetNameContext = createContext<((named: Named) => void) | null>(null);

export function PageTrailProvider({ children }: { children: ReactNode }) {
  const [named, setNamed] = useState<Named>({});
  return (
    <SetNameContext.Provider value={setNamed}>
      <NameContext.Provider value={named}>{children}</NameContext.Provider>
    </SetNameContext.Provider>
  );
}

/** Names the page in the top bar's breadcrumb while it's mounted. An
 *  undefined name, or none yet while the record loads, leaves the route's
 *  label. `via` is the page's own ancestry, outermost first (see
 *  `pageTrail`); without it the parent is the sidebar's. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageTrailName(
  name: string | undefined,
  via?: readonly TrailLink[],
): void {
  const setNamed = useContext(SetNameContext);
  // Keyed by content, so a caller passing a fresh array each render does not
  // set the name on every render.
  const viaKey = JSON.stringify(via ?? []);
  useEffect(() => {
    if (!setNamed) return;
    const links = JSON.parse(viaKey) as TrailLink[];
    setNamed({ name, via: links.length > 0 ? links : undefined });
    return () => setNamed({});
  }, [setNamed, name, viaKey]);
}

/** The name the current page gave, for TopBarTrail. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageTrailNameValue(): string | undefined {
  return useContext(NameContext).name;
}

/** The ancestry the current page gave, if any, for TopBarTrail. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageTrailVia(): readonly TrailLink[] | undefined {
  return useContext(NameContext).via;
}
